import { html, css, LitElement } from "lit";
import { customElement } from "lit/decorators.js";
import { property, state } from "lit/decorators.js";
import type { SplitPanelConfig } from "../../types/ead-types.js";

@customElement("ead-split-panel-layout")
export class EadSplitPanelLayout extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
      width: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .split-container {
      display: flex;
      height: 100%;
      width: 100%;
      min-height: 0;
      position: relative;
    }

    .panel {
      overflow: hidden;
      position: relative;
      transition: all 0.2s ease;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .panel-left {
      flex-shrink: 0;
    }

    .panel-right {
      flex: 1;
      min-width: 0;
    }

    .divider {
      width: 4px;
      background: var(--border, #2e2f33);
      cursor: col-resize;
      position: relative;
      flex-shrink: 0;
      transition: background 0.2s;
      z-index: 10;
    }

    .divider:hover {
      background: var(--accent, #6ea8fe);
    }

    .divider.dragging {
      background: var(--accent, #6ea8fe);
    }

    .divider-handle {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 20px;
      height: 40px;
      background: var(--bg-surface, #222326);
      border: 1px solid var(--border, #2e2f33);
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .divider:hover .divider-handle {
      opacity: 1;
    }

    .divider-handle::after {
      content: "";
      width: 2px;
      height: 16px;
      background: var(--muted, #8b8d93);
      border-radius: 1px;
      box-shadow: 4px 0 0 var(--muted, #8b8d93), -4px 0 0 var(--muted, #8b8d93);
    }

    .collapse-btn {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      border: none;
      background: var(--bg-surface, #222326);
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--muted, #8b8d93);
      font-size: 10px;
      transition: all 0.2s;
      z-index: 20;
    }

    .collapse-btn:hover {
      background: var(--accent, #6ea8fe);
      color: white;
    }

    .collapse-btn.left {
      left: -8px;
    }

    .collapse-btn.right {
      right: -8px;
    }

    .panel-content {
      height: 100%;
      width: 100%;
      min-height: 0;
      flex: 1 1 auto;
      overflow: auto;
    }

    .collapsed {
      display: none;
    }

    .expand-hint {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: var(--muted, #8b8d93);
      font-size: 12px;
      text-align: center;
      pointer-events: none;
    }

    @media (max-width: 768px) {
      .split-container {
        flex-direction: column;
      }

      .divider {
        width: 100%;
        height: 4px;
        cursor: row-resize;
      }

      .panel-left {
        width: 100% !important;
      }

      .panel-right {
        width: 100% !important;
      }
    }
  `;

  @property({ type: Number }) splitRatio = 0.6;
  @property({ type: Number }) minLeftWidth = 300;
  @property({ type: Number }) minRightWidth = 300;
  @property({ type: Number }) minSplitRatio = 0.1;
  @property({ type: Number }) maxSplitRatio = 0.9;
  @property({ type: Boolean }) resizable = true;
  @property({ type: Boolean }) collapsible = true;
  @property() defaultCollapsed: 'left' | 'right' | 'none' = 'none';

  @state() private _leftCollapsed = false;
  @state() private _rightCollapsed = false;
  @state() private _isDragging = false;
  @state() private _leftWidth = 0;

  connectedCallback() {
    super.connectedCallback();
    this._updateWidths();

    // Set default collapsed state
    if (this.defaultCollapsed === 'left') {
      this._leftCollapsed = true;
    } else if (this.defaultCollapsed === 'right') {
      this._rightCollapsed = true;
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('splitRatio')) {
      this._updateWidths();
    }
  }

  private _updateWidths() {
    const containerWidth = this.clientWidth || 1200;
    this._leftWidth = this._clampLeftWidth(containerWidth * this.splitRatio, containerWidth);
  }

  private _clampLeftWidth(nextWidth: number, containerWidth: number): number {
    const ratioMin = Math.max(0.05, Math.min(this.minSplitRatio, 0.95));
    const ratioMax = Math.max(ratioMin, Math.min(this.maxSplitRatio, 0.95));

    const ratioBoundMin = containerWidth * ratioMin;
    const ratioBoundMax = containerWidth * ratioMax;
    const pixelBoundMin = Math.max(0, this.minLeftWidth);
    const pixelBoundMax = Math.max(0, containerWidth - this.minRightWidth);

    const lowerBound = Math.max(ratioBoundMin, pixelBoundMin);
    const upperBound = Math.max(lowerBound, Math.min(ratioBoundMax, pixelBoundMax));
    return Math.floor(Math.max(lowerBound, Math.min(nextWidth, upperBound)));
  }

  private _handleMouseDown(e: MouseEvent) {
    if (!this.resizable) return;
    e.preventDefault();

    this._isDragging = true;
    const startX = e.clientX;
    const startWidth = this._leftWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!this._isDragging) return;

      const deltaX = moveEvent.clientX - startX;
      const newWidth = startWidth + deltaX;
      const containerWidth = this.clientWidth || 1200;

      const clampedWidth = this._clampLeftWidth(newWidth, containerWidth);

      this._leftWidth = clampedWidth;
      this.splitRatio = clampedWidth / containerWidth;
    };

    const handleMouseUp = () => {
      this._isDragging = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  private _toggleLeft() {
    if (!this.collapsible) return;
    this._leftCollapsed = !this._leftCollapsed;
    if (this._leftCollapsed) {
      this._rightCollapsed = false;
    }
  }

  private _toggleRight() {
    if (!this.collapsible) return;
    this._rightCollapsed = !this._rightCollapsed;
    if (this._rightCollapsed) {
      this._leftCollapsed = false;
    }
  }

  render() {
    const containerWidth = this.clientWidth || 1200;
    const leftWidth = this._leftCollapsed ? 0 : this._leftWidth;
    const rightWidth = this._rightCollapsed ? 0 : containerWidth - leftWidth - (this.resizable ? 4 : 0);

    return html`
      <div class="split-container">
        <div class="panel panel-left ${this._leftCollapsed ? 'collapsed' : ''}" style="width: ${leftWidth}px;">
          <slot name="left">
            <div class="panel-content">
              <div class="expand-hint">Left panel content</div>
            </div>
          </slot>
          ${this.collapsible ? html`
            <button class="collapse-btn right" @click=${this._toggleLeft} title="${this._leftCollapsed ? 'Expand' : 'Collapse'}">
              ${this._leftCollapsed ? '▶' : '◀'}
            </button>
          ` : ''}
        </div>

        ${this.resizable && !this._leftCollapsed && !this._rightCollapsed ? html`
          <div class="divider ${this._isDragging ? 'dragging' : ''}" @mousedown=${this._handleMouseDown}>
            <div class="divider-handle"></div>
          </div>
        ` : ''}

        <div class="panel panel-right ${this._rightCollapsed ? 'collapsed' : ''}" style="width: ${rightWidth}px;">
          <slot name="right">
            <div class="panel-content">
              <div class="expand-hint">Right panel content</div>
            </div>
          </slot>
          ${this.collapsible ? html`
            <button class="collapse-btn left" @click=${this._toggleRight} title="${this._rightCollapsed ? 'Expand' : 'Collapse'}">
              ${this._rightCollapsed ? '◀' : '▶'}
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-split-panel-layout": EadSplitPanelLayout;
  }
}
